// F3: a newly observed summon inherits its controller's active combat. Rules remain page-owned.
import { resolveEngineFiredCasts, type AbilityUseReaders, type AbilityUseVerdict } from '../actions/ability-use.js';
import { ecId, record, upper, type Row } from '../ability/context.js';
import type { RollSource } from '../actions/roll-source.js';

const scalar = (v: unknown): unknown => Array.isArray(v) ? v[0] : v;
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
export const summonRoster = (e: Row): Row[] => (Array.isArray(e.encounter_creatures_arr) ? e.encounter_creatures_arr : []).filter((r): r is Row => !!record(r));
export function midCombatSummon(e: Row, id: string): Row | null {
  const b = record(e.encounters) ?? e, combat = upper(b.active_combat_id), eid = upper(b.encounter_id);
  if (Number(scalar(b.in_combat)) !== 1 || !UUID.test(combat) || !UUID.test(eid)) return null;
  const all = summonRoster(e), summon = all.find(r => ecId(r) === upper(id));
  const s = record(summon?.encounter_creatures), controller = upper(s?.controller_encounter_creature_id);
  const c = record(all.find(r => ecId(r) === controller)?.encounter_creatures);
  if (!summon || !s || !c || controller === upper(id) || !UUID.test(controller)) return null;
  for (const row of [s, c]) if (upper(row.encounter_id) !== eid || upper(row.combat_id) !== combat || Number(scalar(row.encounter_creature_deleted) ?? 0) !== 0) return null;
  return summon;
}
/** A full initial snapshot establishes the baseline; entering combat never re-fires older summons. */
export function newMidCombatSummons(before: ReadonlySet<string> | undefined, e: Row, previousCombat?: string): string[] {
  if (!before) return [];
  if (previousCombat !== undefined && previousCombat !== upper((record(e.encounters) ?? e).active_combat_id)) return [];
  return summonRoster(e).map(ecId).filter(id => !before.has(id) && !!midCombatSummon(e, id));
}
export async function summonBeginsOperationId(id: string): Promise<string> {
  const h = [...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode('summon-begins|' + upper(id))))].map(n => n.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-8${h.slice(17, 20)}-${h.slice(20, 32)}`.toUpperCase();
}
type Resolved = Extract<AbilityUseVerdict, { kind: 'resolved' }>;
/** Match R-130's delivery boundary, and the ability commit's anchor row binding (lane A14: a non-first carrier's anchor must be an ability
 *  authored on the summoned creature's own definition — the commit checks the same against the definition row's layers). */
function deliveryGap(v: Resolved, eid: string, summon: Row): string | null {
  if (v.inventory.length) return 'inventory';
  if (v.deathTail.length) return 'death-tail';
  if (v.concentrationBreaks.length) return 'concentration-break';
  if (v.counterspell) return 'counterspell';
  if (v.turnTransition || v.turnDeathNone) return 'turn-transition';
  if ((v as unknown as { forcedMovements?: unknown[] }).forcedMovements?.length) return 'forced-movement';
  if (v.rows.some(r => r.random_table != null || ['random_table', 'random_loot_table', 'random_encounter_table', 'random_name_table'].includes(r.roll_type_1))) return 'random-table-row';
  const anchors = v.statusesAdded.filter(s => Number(s.status_aoe_source ?? 0) === 1);
  if (anchors.some(s => upper(s.target) === upper(eid) || !UUID.test(upper(s.status_aoe_type)))) return 'zone-anchor';
  const first = anchors.length ? upper(anchors[0]!.status_origin_id || anchors[0]!.status_item_id) : '';
  const authored = upper(JSON.stringify(record(summon.item_layer_types) ?? {}));
  if (anchors.some(s => { const a = upper(s.status_origin_id || s.status_item_id); return a !== first && !authored.includes(a); })) return 'anchor-carrier-not-authored';
  if (v.rows.some(r => upper(r.roll_to) === upper(eid) || upper(r.roll_by) === upper(eid))) return 'encounter-row';
  return null;
}
/** Delivery gaps, including extreme branches, are named before the real RollSource can be touched. */
export function resolveSummonBegins(eid: string, id: string, e: Row, members: ReadonlyMap<string, Row>, read: AbilityUseReaders, roll: RollSource): AbilityUseVerdict {
  if (!midCombatSummon(e, id)) return { kind: 'handback', reason: 'summon-begins:stale' };
  const input = { encounterId: upper(eid), afterCombatBegins: [upper(id)] };
  for (const high of [false, true]) {
    const dry = resolveEngineFiredCasts(input, e, members, read, { d: die => high ? die : 1, fraction: () => high ? 0.999999 : 0 });
    if (dry.kind !== 'resolved') return dry;
    const gap = deliveryGap(dry, eid, midCombatSummon(e, id)!);
    if (gap) return { kind: 'handback', reason: 'summon-begins:unsavable:' + gap };
  }
  return resolveEngineFiredCasts(input, e, members, read, roll);
}
export function summonBeginsAbilityId(v: Resolved, summon: Row): string {
  const anchor = v.statusesAdded.find(s => Number(s.status_aoe_source ?? 0) === 1);
  return upper(anchor ? anchor.status_origin_id || anchor.status_item_id : v.rows[0]?.roll_item_id ?? record(summon.encounter_creatures)?.item_id);
}
